import type { ClientPortfolioResponse } from '../../../../types';

interface TransformedData {
  clientName: string;
  unifiedId: string;
  segment?: string;
  advisor?: string;
  status: 'Active' | 'Inactive' | 'Restricted' | 'Closed';
  lastUpdated: string;
  totalMarketValue: number;
  unrealizedGainLoss: number;
  holdingsCount: number;
  accountsCount: number;
  lastCalculated: string;
  assetAllocation: Array<{ name: string; value: number; percentage: number }>;
  accounts: Array<{
    account_name: string;
    account_id: string;
    source?: string;
    account_type: string;
    market_value?: number;
  }>;
  holdings: Array<{
    symbol: string;
    security_name: string;
    quantity?: number;
    market_value: number;
  }>;
  dataTrust: {
    trustPercentage: number;
    sources: string[];
  };
  conflicts: Array<{
    field: string;
    field_label: string;
    source_value: string | number;
    new_value?: string | number;
    resolved_value: string | number;
    strategy: string;
    sourceAName?: string;
    sourceBName?: string;
  }>;
  trustMetrics: {
    totalFieldsCompared: number;
    matchingFields: number;
    resolvedFields: number;
    missingFields: number;
    lastUpdated: string;
  };
  fieldTrust: Array<{
    field: string;
    unifiedValue: string | number;
    status: 'Active' | 'Missing';
  }>;
  attributeDefinitionsCount: number;
  sourceComparisonCount: number;
  portfolioAccounts: any[];
  clientIdentifierIds: string[];
}

export const transformPortfolioData = (apiData: ClientPortfolioResponse): TransformedData => {
  const { data, data_quality } = apiData;
  const dataRecord = data as Record<string, unknown>;
  const client: Record<string, unknown> = (dataRecord?.client as Record<string, unknown>) || {};
  const portfolioAccounts: unknown[] = Array.isArray(dataRecord?.portfolio) ? dataRecord.portfolio : [];

  // Get ALL client accounts (not just portfolio accounts)
  const allClientAccounts: unknown[] = Array.isArray(client?.accounts) ? client.accounts as unknown[] : [];

  const allHoldings: Array<{
    symbol: string;
    security_name: string;
    quantity?: number;
    market_value: number;
  }> = [];

  let totalEquityValue = 0;
  let totalFixedIncomeValue = 0;
  let totalCashValue = 0;
  let totalOtherValue = 0;
  let totalMarketValue = 0;
  let totalUnrealizedGainLoss = 0;

  portfolioAccounts.forEach((portfolioAccount: unknown) => {
    const acc = portfolioAccount as Record<string, unknown>;

    // Calculate values from individual holdings instead of summary
    let accountMarketValue = 0;
    let accountGainLoss = 0;

    if (Array.isArray(acc.holdings)) {
      (acc.holdings as unknown[]).forEach((holding: unknown) => {
        if (holding && typeof holding === 'object') {
          const h = holding as {
            symbol?: string;
            security_name?: string;
            quantity?: number;
            market_value?: number;
            unrealized_gain_loss?: number;
            asset_class?: string;
          };

          if (h.symbol && h.security_name && h.market_value !== undefined) {
            // Sum up market value from individual holdings
            accountMarketValue += h.market_value;

            // Sum up unrealized gain/loss from individual holdings
            if (h.unrealized_gain_loss !== undefined) {
              accountGainLoss += h.unrealized_gain_loss;
            }

            // Calculate asset allocation from actual asset_class
            const assetClass = (h.asset_class || '').toLowerCase();
            const marketValue = h.market_value;

            if (assetClass === 'equity' || assetClass === 'domestic_equity') {
              totalEquityValue += marketValue;
            } else if (assetClass === 'fixed income' || assetClass === 'bond' || assetClass === 'bonds') {
              totalFixedIncomeValue += marketValue;
            } else if (assetClass === 'cash' || assetClass === 'cash equivalent') {
              totalCashValue += marketValue;
            } else if (assetClass && assetClass !== '') {
              totalOtherValue += marketValue;
            }

            allHoldings.push({
              symbol: h.symbol,
              security_name: h.security_name,
              quantity: h.quantity,
              market_value: h.market_value
            });
          }
        }
      });
    }

    // Add account totals to overall totals
    totalMarketValue += accountMarketValue;
    totalUnrealizedGainLoss += accountGainLoss;

    console.log('Portfolio Account Debug (from holdings):', {
      account_identifier: acc.account_identifier,
      holdings_count: Array.isArray(acc.holdings) ? acc.holdings.length : 0,
      account_market_value: accountMarketValue,
      account_unrealized_gain_loss: accountGainLoss,
      running_market_value_total: totalMarketValue,
      running_gain_loss_total: totalUnrealizedGainLoss
    });
  });

  const assetAllocation = [];
  const allocatedValue = totalEquityValue + totalFixedIncomeValue + totalCashValue + totalOtherValue;
  // Use allocatedValue as denominator so percentages always sum to 100%
  const allocationBase = allocatedValue > 0 ? allocatedValue : 1;

  if (totalEquityValue > 0) {
    assetAllocation.push({
      name: 'Equity',
      value: totalEquityValue,
      percentage: (totalEquityValue / allocationBase) * 100
    });
  }
  if (totalFixedIncomeValue > 0) {
    assetAllocation.push({
      name: 'Fixed Income',
      value: totalFixedIncomeValue,
      percentage: (totalFixedIncomeValue / allocationBase) * 100
    });
  }
  if (totalCashValue > 0) {
    assetAllocation.push({
      name: 'Cash',
      value: totalCashValue,
      percentage: (totalCashValue / allocationBase) * 100
    });
  }
  // Always include Other, even if value is 0
  assetAllocation.push({
    name: 'Other',
    value: totalOtherValue,
    percentage: totalOtherValue > 0 ? (totalOtherValue / allocationBase) * 100 : 0
  });

  // Normalize so percentages exactly sum to 100 (fix floating-point drift)
  if (assetAllocation.length > 0) {
    const totalPct = assetAllocation.reduce((sum, a) => sum + a.percentage, 0);
    if (totalPct > 0 && Math.abs(totalPct - 100) > 0.01) {
      const factor = 100 / totalPct;
      assetAllocation.forEach(a => { a.percentage = a.percentage * factor; });
    }
    // Ensure last item absorbs any remaining rounding so sum is exactly 100
    const sumExceptLast = assetAllocation.slice(0, -1).reduce((s, a) => s + Math.round(a.percentage * 10) / 10, 0);
    assetAllocation[assetAllocation.length - 1].percentage = Math.round((100 - sumExceptLast) * 10) / 10;
  }

  const firstName = (client?.first_name as string) || '';
  const lastName = (client?.last_name as string) || '';
  const clientName = `${firstName} ${lastName}`.trim() || 'Unknown Client';

  const sources: string[] = Array.isArray(client?.source_list) ? client.source_list as string[] : [];
  const clientIdentifierIds: string[] = Array.isArray(client?.client_identifier_ids) ? client.client_identifier_ids as string[] : [];
  const dataQuality = (data_quality as unknown) as Record<string, unknown>;
  const trustPercentage = (dataQuality?.trust_score as number) ?? 0;

  // Extract conflicts from API response
  const processingDetails = (dataRecord.processing_details as Record<string, unknown>) || {};

  // Extract attribute definitions count from processing_details.attribute_definitions array
  const attributeDefinitions = Array.isArray(processingDetails.attribute_definitions)
    ? processingDetails.attribute_definitions
    : [];
  const attributeDefinitionsCount = attributeDefinitions.length;

  // Extract source comparison count from job_statistics.conflicts_resolved_count
  const jobStatistics = (processingDetails.job_statistics as Record<string, unknown>) || {};
  const sourceComparisonCount = (jobStatistics.conflicts_resolved_count as number) ?? 0;


  // Extract conflicts from client processing details
  const clientProcessingDetails = (processingDetails.client_processing_details as Record<string, unknown>) || {};
  const records = Array.isArray(clientProcessingDetails.records) ? clientProcessingDetails.records : [];

  const conflicts: Array<{
    field: string;
    field_label: string;
    source_value: string | number;
    new_value?: string | number;
    resolved_value: string | number;
    strategy: string;
    sourceAName?: string;
    sourceBName?: string;
  }> = [];

  // Extract all conflicts from all records
  records.forEach((record: unknown) => {
    if (record && typeof record === 'object') {
      const recordObj = record as Record<string, unknown>;
      const conflictDetails = (recordObj.conflict_details as Record<string, unknown>) || {};
      const conflictList = Array.isArray(conflictDetails.conflicts) ? conflictDetails.conflicts : [];

      conflictList.forEach((conflict: unknown) => {
        if (conflict && typeof conflict === 'object') {
          const conflictObj = conflict as {
            field?: string;
            field_label?: string;
            source_value?: string | number;
            source_data_source?: string;
            new_value?: string | number;
            new_data_source?: string;
            resolved_value?: string | number;
            strategy?: string;
          };

          conflicts.push({
            field: conflictObj.field || '',
            field_label: conflictObj.field_label || '',
            source_value: conflictObj.source_value || '',
            new_value: conflictObj.new_value,
            resolved_value: conflictObj.resolved_value || '',
            strategy: conflictObj.strategy || '',
            sourceAName: conflictObj.source_data_source || 'Source 1',
            sourceBName: conflictObj.new_data_source || 'Source 2'
          });
        }
      });
    }
  });

  const fieldTrust: Array<{
    field: string;
    unifiedValue: string | number;
    status: 'Active' | 'Missing';
  }> = [
    {
      field: 'Total Market Value',
      unifiedValue: totalMarketValue > 0 ? `$${totalMarketValue.toLocaleString()}` : 'Missing',
      status: totalMarketValue > 0 ? 'Active' : 'Missing'
    },
    {
      field: 'Client Identifier',
      unifiedValue: (client?.client_identifier as string) || 'Missing',
      status: (client?.client_identifier as string) ? 'Active' : 'Missing'
    },
    {
      field: 'Holdings Count',
      unifiedValue: allHoldings.length.toString(),
      status: allHoldings.length > 0 ? 'Active' : 'Missing'
    },
    {
      field: 'Account Count',
      unifiedValue: allClientAccounts.length.toString(),
      status: allClientAccounts.length > 0 ? 'Active' : 'Missing'
    },
    {
      field: 'Tax Status',
      unifiedValue: (client?.tax_status as string) || 'Missing',
      status: (client?.tax_status as string) ? 'Active' : 'Missing'
    },
    {
      field: 'Risk Profile',
      unifiedValue: (client?.risk_profile as string) || 'Missing',
      status: (client?.risk_profile as string) ? 'Active' : 'Missing'
    }
  ];

  // Calculate correct metrics
  const totalFieldsCompared = attributeDefinitionsCount; // 35 (total attributes)

  // Get unique fields that had conflicts (not total conflict count)
  const uniqueConflicts = conflicts.filter((conflict, index, self) =>
    index === self.findIndex((c) => c.field_label === conflict.field_label)
  );
  const resolvedFields = uniqueConflicts.length; // Unique fields that needed resolution

  // Missing fields from fieldTrust (for display purposes only)
  const missingFields = fieldTrust.filter(f => f.status === 'Missing').length;

  // Matching fields are those that didn't have conflicts and aren't missing
  const matchingFields = Math.max(0, totalFieldsCompared - resolvedFields - missingFields);


  const clientStatus = client?.is_active ? 'Active' : 'Inactive';
  const updatedAt = (client?.updated_at as string) || '';
  const lastUpdated = updatedAt || new Date().toISOString();

  return {
    clientName,
    unifiedId: (client?.client_identifier as string) || '-',
    segment: undefined,
    advisor: undefined,
    status: clientStatus as 'Active' | 'Inactive',
    lastUpdated,
    totalMarketValue,
    unrealizedGainLoss: totalUnrealizedGainLoss,
    holdingsCount: allHoldings.length,
    accountsCount: allClientAccounts.length, // Use ALL client accounts
    lastCalculated: updatedAt || new Date().toISOString(),
    assetAllocation,
    accounts: allClientAccounts.map((acc: unknown) => {
      if (acc && typeof acc === 'object') {
        const account = acc as {
          account_name?: string;
          account_identifier?: string;
          account_type?: string;
          amount?: number;
          id?: number;
        };

        const source = account.account_identifier?.startsWith('CA-') ? 'Custodian A' :
                       account.account_identifier?.startsWith('CB-') ? 'Custodian B' : undefined;

        // Use amount from client accounts data
        const marketValue = account.amount;

        return {
          account_name: account.account_name || '-',
          account_id: account.account_identifier || '-',
          source,
          account_type: account.account_type || '-',
          market_value: marketValue
        };
      }
      return {
        account_name: '-',
        account_id: '-',
        account_type: '-'
      };
    }),
    holdings: allHoldings,
    dataTrust: {
      trustPercentage,
      sources
    },
    conflicts,
    trustMetrics: {
      totalFieldsCompared,
      matchingFields,
      resolvedFields,
      missingFields,
      lastUpdated: (dataQuality?.last_updated as string) || new Date().toLocaleDateString()
    },
    fieldTrust,
    attributeDefinitionsCount,
    sourceComparisonCount,
    portfolioAccounts,
    clientIdentifierIds
  };
};
